<template>
  <Transition name="popup">
    <div
      v-show="isDisplayed"
      ref="pickerEl"
      class="destination-picker"
      :style="computedStyle"
      tabindex="0"
      @keydown.down.prevent.stop="navigate(1)"
      @keydown.up.prevent.stop="navigate(-1)"
      @keydown.enter.prevent.stop="selectCurrent"
      @keydown.escape.stop="closePicker"
    >
      <div class="destination-input">
        <input
          ref="inputEl"
          v-model="queryText"
          type="text"
          class="destination-search"
          :placeholder="placeholder"
          @input="onInput"
        />
      </div>
      <ul v-if="completions.length" class="destination-results">
        <template
          v-for="(completion, i) in completions"
          :key="`destination-${i}-${completion.text}`"
        >
          <Suspense>
            <ConsoleCompletion
              class="destination-result"
              :class="{ selected: i === selected }"
              :completion="completion"
              :selected="i === selected"
              :no-completion-helpers="true"
              @mouseover="selected = i"
              @click="select(completion)"
            />
          </Suspense>
        </template>
      </ul>
      <div v-else-if="queryText.trim()" class="destination-empty">
        No results
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";

import type {
  MandateDestination,
  MandateParserWrapper,
  RoutineMandateCompletion,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import ConsoleCompletion from "@/components/ConsoleCompletion.vue";
import { hasNotesProperty } from "@/components/graph/utils";
import * as inject from "@/components/inject";
import { makeParser, parseWithMode } from "@/components/parsers";
import { isParentId } from "@/components/search";
import { getDefaultResults } from "@/components/search/defaults";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import { useOverlayPresence } from "@/composables/useOverlayPresence";

// What the caller wants to pick, plus the filtering rules mirrored from the
// console's destination mode.
export type DestinationPickerOptions = {
  workspaceId: WorkspaceId;
  // Destinations never offered, matched by document id: typically the item
  // being moved (never its own destination) and its current parent.
  excludeDestinationIds?: string[];
  // Search input placeholder; defaults to the move flow's "Move to…" (e.g. the
  // console's destination pill picks a parent, not a move target).
  placeholder?: string;
};

// The picker's callback is invoked exactly once: with the chosen destination on
// selection, or `null` when the picker is dismissed without a choice.
type SelectCallback = (destination: MandateDestination | null) => void;

const RESULT_LIMIT = 8;
// Fallback placement used when the picker has no anchor element (keyboard
// shortcut with the overflow menu closed): top-right, just below the top bar.
const TOP_BAR_OFFSET = 52;
const VIEWPORT_MARGIN = 12;

export default defineComponent({
  name: "DestinationPicker",
  components: { ConsoleCompletion },
  setup() {
    const controller = inject.controller();
    const isInputFocused = inject.isInputFocused();
    const profile = inject.profile();

    const isDisplayed = ref(false);
    const releaseOverlayPresence = useOverlayPresence(isDisplayed);
    onBeforeUnmount(releaseOverlayPresence);
    const pickerEl = ref<HTMLElement | null>(null);
    const inputEl = ref<HTMLInputElement | null>(null);
    const computedStyle = ref({});
    const currentPosition = ref<"left" | "right">("left");
    const triggerEl = ref<HTMLElement | undefined>(undefined);
    const triggerRect = ref<DOMRect | null>(null);
    const placeholder = ref("Move to…");

    const queryText = ref("");
    const completions = ref<RoutineMandateCompletion[]>([]);
    const selected = ref(0);

    const parser = ref<MandateParserWrapper>();
    const options = ref<DestinationPickerOptions | null>(null);
    const onSelect = ref<SelectCallback | null>(null);
    // Ensures the callback fires exactly once per invocation (select or cancel).
    let settled = false;

    // Mirrors ParsedInput's destination filter so both entry points agree on
    // which destinations are offered.
    const keep = (completion: RoutineMandateCompletion): boolean => {
      const value = completion.value;
      if (value.type !== "destination") return false;
      const entry = value.value;
      // Calendars cannot receive a document.
      if (entry.type !== "document") return false;
      // Never offer the excluded destinations (typically the item being
      // moved and its current parent).
      return !options.value?.excludeDestinationIds?.includes(entry.id.id);
    };

    // A custom object can only receive anything (task blocks, object embeds)
    // through a notes-based property, so objects of types without one are not
    // valid destinations. Built-in destinations (tasks, pages, events, …)
    // always can.
    const canReceiveInNotes = async (
      completion: RoutineMandateCompletion
    ): Promise<boolean> => {
      const value = completion.value;
      if (
        value.type !== "destination" ||
        value.value.type !== "document" ||
        value.value.id.kind !== "object"
      ) {
        return true;
      }
      const parsed = await controller.graph.object.id.parse(value.value.id.id);
      if (!parsed.workspace_id || !parsed.table_id) return false;
      // An object destination must live in the picker's workspace: neither a
      // move nor a console destination can cross workspace bounds. Searched
      // completions are already scoped by the backend; this mainly filters
      // the defaults (e.g. a bound task of today's agenda whose row lives in
      // another organization's workspace).
      if (parsed.workspace_id !== options.value?.workspaceId) return false;
      const database = (
        await controller.graph.database.get(
          parsed.workspace_id,
          parsed.table_id
        )
      ).release();
      return hasNotesProperty(database);
    };

    // The destinations to offer before the user has typed anything: the same
    // "most likely targets" as the editor's reference suggestions (today's
    // tasks and events in the personal workspace, the home object elsewhere),
    // minus the entries that are not documents (databases) and thus cannot
    // receive a move. They then go through the same `keep` /
    // `canReceiveInNotes` filters as searched destinations.
    const defaultCompletions = async (): Promise<
      RoutineMandateCompletion[]
    > => {
      const opts = options.value;
      if (!opts) return [];
      const organizationId = getOrganizationIdFromWorkspaceId(
        profile.value,
        opts.workspaceId
      );
      if (!organizationId) return [];
      const references = await getDefaultResults({
        controller,
        profile,
        organizationId,
        workspaceId: opts.workspaceId,
      });
      return references.flatMap((reference) =>
        isParentId(reference.id)
          ? [
              {
                text: reference.title,
                value: {
                  type: "destination" as const,
                  value: { type: "document" as const, id: reference.id },
                },
              },
            ]
          : []
      );
    };

    // Guards against out-of-order async refreshes clobbering a newer result.
    let refreshToken = 0;
    const refresh = async () => {
      if (!parser.value || !options.value) return;
      const token = ++refreshToken;
      const result = queryText.value.trim()
        ? await parseWithMode(
            controller,
            parser.value,
            "destination",
            queryText.value,
            undefined,
            RESULT_LIMIT,
            options.value.workspaceId
          )
        : await defaultCompletions();
      if (token !== refreshToken) return;
      if (!Array.isArray(result)) {
        completions.value = [];
        return;
      }
      const kept = result.filter(keep);
      const receivable = await Promise.all(kept.map(canReceiveInNotes));
      if (token !== refreshToken) return;
      completions.value = kept
        .filter((_, i) => receivable[i])
        .slice(0, RESULT_LIMIT);
      selected.value = 0;
    };

    let inputTimer: ReturnType<typeof setTimeout> | undefined;
    const onInput = () => {
      if (inputTimer) clearTimeout(inputTimer);
      inputTimer = setTimeout(() => void refresh(), 120);
    };

    const navigate = (direction: number) => {
      if (!completions.value.length) return;
      const next = selected.value + direction;
      selected.value = Math.max(
        0,
        Math.min(next, completions.value.length - 1)
      );
    };

    const settle = (destination: MandateDestination | null) => {
      if (settled) return;
      settled = true;
      onSelect.value?.(destination);
    };

    const select = (completion: RoutineMandateCompletion) => {
      const value = completion.value;
      if (value.type !== "destination") return;
      settle(value.value);
      hide();
    };

    const selectCurrent = () => {
      const completion = completions.value[selected.value];
      if (completion) select(completion);
    };

    const hide = () => {
      isDisplayed.value = false;
      isInputFocused.value = false;
      document.removeEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
    };

    // Public close (cancel): dismiss and report "no selection".
    const closePicker = () => {
      settle(null);
      hide();
    };

    const showPicker = async (
      element: HTMLElement | undefined,
      pickerOptions: DestinationPickerOptions,
      callback: SelectCallback,
      position?: "left" | "right",
      anchorRect?: DOMRect
    ) => {
      triggerEl.value = element;
      triggerRect.value = anchorRect || null;
      currentPosition.value = position || "left";
      options.value = pickerOptions;
      placeholder.value = pickerOptions.placeholder ?? "Move to…";
      onSelect.value = callback;
      settled = false;
      queryText.value = "";
      completions.value = [];
      selected.value = 0;
      parser.value = await makeParser(controller, "destination");

      computedStyle.value = {
        position: "fixed",
        zIndex: 1000,
        opacity: "0",
        pointerEvents: "none",
      };
      isDisplayed.value = true;
      isInputFocused.value = true;

      await nextTick();
      await updatePosition();
      computedStyle.value = {
        ...computedStyle.value,
        opacity: "1",
        pointerEvents: "auto",
      };
      document.addEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
      nextTick(() => {
        pickerEl.value?.focus();
        inputEl.value?.focus();
      });
      await refresh();
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (!isDisplayed.value) return;
      const target = event.target;
      const insidePicker =
        target instanceof Node && pickerEl.value?.contains(target);
      const insideTrigger =
        target instanceof Node && triggerEl.value?.contains(target);
      if (!insidePicker && !insideTrigger) {
        event.stopPropagation();
        event.preventDefault();
        closePicker();
      }
    };

    const updatePosition = async () => {
      await nextTick();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      // Prefer the live trigger element; fall back to the captured anchor rect
      // (e.g. when the trigger menu has already closed); as a last resort, open
      // in the top-right corner like the "..." overflow menu.
      let rect: DOMRect | null = null;
      if (triggerEl.value && document.body.contains(triggerEl.value)) {
        rect = triggerEl.value.getBoundingClientRect();
      } else if (triggerRect.value) {
        rect = triggerRect.value;
      }
      // An off-screen anchor (e.g. the stale wrapper of a *closed* "..." overflow
      // menu, whose rect is pinned to the origin with `right`/`bottom` ~0) would
      // push the pop-up off-screen. Treat such a rect as "no anchor". Note we do
      // NOT require a non-zero size: right-click anchors are intentionally 0x0
      // elements placed at the click point and must stay usable.
      const isRectUsable =
        !!rect &&
        rect.right > 0 &&
        rect.bottom > 0 &&
        rect.left < viewportWidth &&
        rect.top < viewportHeight;
      if (!rect || !isRectUsable) {
        // No usable anchor (e.g. a keyboard shortcut where the "..." menu is
        // closed): mimic the "..." overflow menu and open in the top-right
        // corner, just under the top bar, rather than centering on screen.
        computedStyle.value = {
          position: "fixed",
          top: `${TOP_BAR_OFFSET}px`,
          right: `${VIEWPORT_MARGIN}px`,
          transform: "none",
          zIndex: 1000,
        };
        return;
      }
      const pickerHeight = pickerEl.value?.offsetHeight || 0;
      const pickerWidth = pickerEl.value?.offsetWidth || 320;
      const margin = 4;
      // Horizontal anchor: align to the trigger's left ("left") or right
      // ("right") edge, then clamp so the fixed-width pop-up stays fully on
      // screen (e.g. a "..." menu near the right edge must not overflow).
      const rawLeft =
        currentPosition.value === "right"
          ? rect.right - pickerWidth
          : rect.left;
      const clampedLeft = Math.max(
        margin,
        Math.min(rawLeft, viewportWidth - pickerWidth - margin)
      );
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const canPlaceBelow = spaceBelow >= pickerHeight + margin;
      const canPlaceAbove = spaceAbove >= pickerHeight + margin;

      if (canPlaceBelow || canPlaceAbove) {
        computedStyle.value = {
          position: "fixed",
          ...(canPlaceBelow
            ? { top: `${rect.bottom + margin}px` }
            : { bottom: `${viewportHeight - rect.top + margin}px` }),
          left: `${clampedLeft}px`,
          transform: "none",
          zIndex: 1000,
        };
        return;
      }

      const verticalCenter = rect.top + rect.height / 2;
      const pickerTop = Math.max(
        margin,
        Math.min(
          verticalCenter - pickerHeight / 2,
          viewportHeight - pickerHeight - margin
        )
      );
      const sideLeft = Math.max(
        margin,
        Math.min(rect.right + margin, viewportWidth - pickerWidth - margin)
      );
      computedStyle.value = {
        position: "fixed",
        top: `${pickerTop}px`,
        left: `${sideLeft}px`,
        transform: "none",
        zIndex: 1000,
      };
    };

    onMounted(() => {
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
    });

    return {
      completions,
      computedStyle,
      closePicker,
      inputEl,
      isDisplayed,
      navigate,
      onInput,
      pickerEl,
      placeholder,
      queryText,
      select,
      selectCurrent,
      selected,
      showPicker,
    };
  },
});
</script>

<style scoped lang="scss">
@include popup-transition();

.destination-picker {
  position: fixed;
  border-radius: $border-radius-l;
  padding: 4px;
  width: 320px;
  transform-origin: top center;
  will-change: transform, opacity;
  @include themed() {
    background: t($bg-tooltip);
    border: 1px solid t($border);
    box-shadow: $tooltip;
    color: t($text);
  }
}

.destination-input {
  padding: 4px;

  .destination-search {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 12px;
    border-radius: $border-radius-m;
    @extend %font-caption-regular;
    @include themed() {
      background: t($bg);
      border: 1px solid t($grey-15);
      color: t($grey-80);
      &::placeholder {
        color: t($grey-50);
        @extend %font-caption-medium;
      }
      &:focus {
        background: t($grey-10);
        border: 1px solid t($grey-10);
      }
    }
  }
}

.destination-results {
  list-style: none;
  margin: 0;
  padding: 4px 0 0 0;
  max-height: 320px;
  overflow-y: auto;
}

.destination-result {
  border-radius: $border-radius-m;
  cursor: default;

  &.selected {
    @include themed() {
      background: t($bg-menu-hover);
    }
  }
}

.destination-empty {
  padding: 12px;
  text-align: center;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
